import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, MetricCard, Panel, SearchField, SegmentedControl, StatusChip, type ListColumn,
} from '../../../ui-kit';
import {
  discardRequest, downloadPsList, fetchCompletedRequests, fetchLiveRequests, watchRequests, type PsRequestCard,
} from '../../../services/policyServicingService';
import QueueRowMenu from '../QueueRowMenu';
import { fromNow, lengthOf, matches, useLive, when } from '../rocco/live';
import ListDownload from '../rocco/ListDownload';
import RequestStoryModal from './RequestStory';
import { statusChip } from './status';

type Scope = 'live' | 'completed';

/**
 * Emails being worked on now — read, matched to a policy, the carrier on the
 * phone, a reply being written or held for approval. Updates the moment one
 * moves: the server says when, and the list fetches then.
 */
export function LiveRequestsPage() {
  return <RequestsPage scope="live" />;
}

/**
 * Emails the agent has finished with: answered in the customer's own thread,
 * or handed to a person with the reason why. Each row carries its call to the
 * carrier too — how long, whom Lina spoke to, the reference — so the email
 * and the conversation are one record, opened together.
 */
export function CompletedRequestsPage() {
  return <RequestsPage scope="completed" />;
}

const FILTERS: Record<Scope, { value: string; label: string; test: (r: PsRequestCard) => boolean }[]> = {
  live: [
    { value: 'all', label: 'All', test: () => true },
    { value: 'calling', label: 'On a call', test: (r) => r.status === 'calling' || r.status === 'call_ended' },
    { value: 'approval', label: 'Waiting for approval', test: (r) => r.status === 'reply_drafted' },
  ],
  completed: [
    { value: 'all', label: 'All', test: () => true },
    { value: 'replied', label: 'Replied', test: (r) => r.status === 'replied' },
    { value: 'needs_person', label: 'Needs a person', test: (r) => r.status === 'needs_person' },
    { value: 'discarded', label: 'Discarded', test: (r) => r.status === 'discarded' },
  ],
};

function RequestsPage({ scope }: { scope: Scope }) {
  const live = scope === 'live';
  const { data, error, loading, streaming, reconnecting, reload } = useLive(
    live ? fetchLiveRequests : fetchCompletedRequests,
    live ? 5000 : 15_000,
    { watch: watchRequests },
  );
  const [open, setOpen] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState('all');
  const all = data?.requests ?? [];
  const filters = FILTERS[scope];
  const chosen = filters.find((f) => f.value === show) ?? filters[0];
  const rows = all.filter(chosen.test).filter((r) => matches(query, [
    r.sender, r.sender_email, r.subject, r.request_label, r.policy_num, r.carrier, r.stage,
    r.employer_name, r.member_name,
    r.answer_summary, r.needs_person_reason, r.reference_number, r.rep_name,
  ]));

  const grouped = all.some((r) => r.employer_name);
  const columns: ListColumn<PsRequestCard>[] = [
    {
      key: 'from',
      sortValue: (r) => r.sender,
      header: 'From',
      grow: 1,
      minWidth: 150,
      render: (r) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{r.sender}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {r.sender_email}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'received',
      sortValue: (r) => r.received_at, sortStartsDescending: true,
      header: 'Received',
      width: 130,
      render: (r) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {when(r.received_at)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fromNow(r.received_at)}</Typography>
        </Stack>
      ),
    },
    {
      key: 'request',
      sortValue: (r) => r.request_label,
      header: 'Request',
      grow: 1.1,
      minWidth: 140,
      render: (r) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {r.request_label || 'Not worked out yet'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{r.subject}</Typography>
        </Stack>
      ),
    },
    {
      key: 'policy',
      sortValue: (r) => r.member_name || r.policy_num,
      // A policyholder writes about their policy; an HR contact about an employee of their group.
      header: grouped ? 'Policy or employee' : 'Policy',
      width: grouped ? 190 : 150,
      render: (r) => (r.employer_name ? (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {r.member_name || 'Employee not worked out'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[r.employer_name, r.carrier].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ) : (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {r.policy_num || '—'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{r.carrier}</Typography>
        </Stack>
      )),
    },
    {
      key: 'status',
      sortValue: (r) => (live ? statusChip(r).label : r.completed_at),
      header: live ? 'Now' : 'Result',
      grow: 0.8,
      minWidth: 170,
      render: (r) => (
        <Stack spacing={0.25} alignItems="flex-start">
          <StatusChip {...statusChip(r)} />
          {live && r.next_try_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              No answer · tries again {when(r.next_try_at)}
            </Typography>
          ) : null}
          {!live && r.completed_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {when(r.completed_at)}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
  ];
  if (live) {
    columns.push({
      key: 'actions',
      header: 'Actions',
      width: 72,
      align: 'right',
      render: (r) => (
        <QueueRowMenu
          what="request"
          onCall={r.status === 'calling' || r.status === 'call_ended'}
          confirmTitle={`Discard ${r.sender}'s request?`}
          confirmText={'It leaves the live list for Completed. No carrier is called about it and no reply is sent. '
            + 'Its story keeps a note that you discarded it.'}
          onDiscard={async () => { await discardRequest(r.id); reload(); }}
        />
      ),
    });
  }
  if (!live) {
    columns.push(
      {
        key: 'outcome',
        sortValue: (r) => r.answer_summary || r.needs_person_reason,
        header: 'What came of it',
        grow: 1.6,
        minWidth: 180,
        render: (r) => (
          <Typography variant="caption" sx={(t) => ({
            color: t.tokens.text.secondary, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          })}>
            {r.status === 'needs_person' ? r.needs_person_reason
              : r.status === 'discarded' ? 'Discarded from the queue. Nothing was called about or replied to after that.'
                : r.answer_summary}
          </Typography>
        ),
      },
      {
        key: 'call',
        sortValue: (r) => r.call_secs, sortStartsDescending: true,
        header: 'Call',
        width: 150,
        render: (r) => (
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
              {r.called ? lengthOf(r.call_secs) : r.answered_by === 'records' ? 'No call needed' : 'No call'}
            </Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {[r.rep_name, r.reference_number && `ref ${r.reference_number}`].filter(Boolean).join(' · ')}
            </Typography>
          </Stack>
        ),
      },
    );
  }

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.id}
      pageSize={25}
      loading={loading}
      onRowClick={(r) => setOpen(r.id)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : live
          ? 'Nothing being worked on. An email to the service inbox appears here within seconds.'
          : 'Nothing finished yet. A request lands here once it is replied to, handed to a person or discarded.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search requests…" />;
  const picker = (
    <SegmentedControl
      value={chosen.value}
      onChange={setShow}
      ariaLabel="Which requests"
      options={filters.map((f) => ({ value: f.value, label: `${f.label} · ${all.filter(f.test).length}` }))}
    />
  );
  const download = <ListDownload download={() => downloadPsList(scope)} total={all.length}
        noun={live ? 'requests being worked on' : 'completed requests'} />;

  const waiting = all.filter((r) => r.status === 'reply_drafted').length;
  const handed = all.filter((r) => r.status === 'needs_person').length;
  const replied = all.filter((r) => r.status === 'replied').length;
  const discarded = all.filter((r) => r.status === 'discarded').length;
  const state = reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself';
  const subtitle = live
    ? `${all.length} being worked on · ${waiting} waiting for approval · ${state}`
    : `${replied} replied · ${handed} handed to a person${discarded ? ` · ${discarded} discarded` : ''} · newest first`;

  const called = all.filter((r) => r.called);
  const timed = called.filter((r) => r.call_secs);
  const average = timed.length
    ? Math.round(timed.reduce((sum, r) => sum + (r.call_secs ?? 0), 0) / timed.length) : null;
  const tiles = live ? null : (
    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
      <MetricCard loading={loading} label="Answered by AI" value={replied.toLocaleString()}
        info="Requests answered in the customer's own email thread." />
      <MetricCard loading={loading} label="Handed to a person" value={handed.toLocaleString()}
        info="Requests the agent would not finish alone, each with its reason." />
      <MetricCard loading={loading} label="Carrier calls by AI" value={called.length.toLocaleString()}
        info="Finished requests where Lina called the carrier, answered or not." />
      <MetricCard loading={loading} label="Average call" value={lengthOf(average)}
        info="How long a call to a carrier lasted, on average, hold time included." />
    </Box>
  );

  return (
    <Stack spacing={2}>
      {tiles}
      <Panel
        title={live ? 'Live requests' : 'Completed requests'}
        subtitle={subtitle}
        search={searchBox}
        tools={picker}
        actions={download}
        expanded={{ content: table, actions: <>{searchBox}{picker}{download}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>
      {open ? <RequestStoryModal requestId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
